/**
 * Pure view helpers for the tabbed admin shell (ACA-145).
 *
 * The active tab is derived from the URL (deep-linking / refresh selects the
 * right tab) and keyboard navigation follows the WAI-ARIA tabs pattern. Both
 * decisions are extracted here so they can be unit-tested without rendering the
 * shell (which pulls in the authenticated header, redux, theme, and router).
 */

/**
 * The index of the active tab for a given pathname, matching a tab when the
 * path equals its route or is nested beneath it. Falls back to 0 (the first
 * tab) when nothing matches — e.g. the `/admin` index before it redirects.
 */
export const activeTabIndex = (
    pathname: string,
    tabPaths: string[]
): number => {
    const index = tabPaths.findIndex(
        (path) => pathname === path || pathname.startsWith(`${path}/`)
    )
    return index < 0 ? 0 : index
}

/**
 * The tab index to move focus/selection to for a keyboard event, or null when
 * the key is not a tab-navigation key. Left/Right wrap around; Home/End jump to
 * the ends.
 */
export const nextTabIndex = (
    key: string,
    current: number,
    count: number
): number | null => {
    if (count <= 0) return null
    switch (key) {
        case 'ArrowRight':
            return (current + 1) % count
        case 'ArrowLeft':
            return (current - 1 + count) % count
        case 'Home':
            return 0
        case 'End':
            return count - 1
        default:
            return null
    }
}
